import { createSlice } from '@reduxjs/toolkit';
import { isBannedUpload, isEnableBanAllUpload } from './functions';
import { getConferenceState } from '../base/conference/functions';
import { getParticipantById } from '../base/participants/functions';
import ReducerRegistry from '../base/redux/ReducerRegistry';
const initialState = {
    enabled: false,
    canUpload: undefined,
    actor: null,
    files: [],
    pendingMessage: null,
    whitelist: {},
    blacklist: {},
};
// Thunks for approving/rejecting uploads and handling moderation
export const approveParticipantUpload = (id) => (dispatch, getState) => {
    const state = getState();
    const { conference } = getConferenceState(state);
    const participant = getParticipantById(state, id);
    if (!participant) {
        return;
    }
    const isBanned = isBannedUpload(participant, state);
    if (isBanned) {
        conference?.vtFileUploadModerationApprove(id);
    }
};
export const rejectParticipantUpload = (id) => (dispatch, getState) => {
    const state = getState();
    const { conference } = getConferenceState(state);
    const participant = getParticipantById(state, id);
    if (!participant) {
        return;
    }
    const isBanned = isBannedUpload(participant, state);
    if (!isBanned) {
        conference?.vtFileUploadModerationReject(id);
    }
};
export const enableBanAllUpload = () => (dispatch, getState) => {
    const state = getState();
    const { conference } = getConferenceState(state);
    const isBanAll = isEnableBanAllUpload(state);
    if (!isBanAll) {
        conference?.enableVTFileUploadModeration();
    }
};
export const disableBanAllUpload = () => (dispatch, getState) => {
    const state = getState();
    const { conference } = getConferenceState(state);
    const isBanAll = isEnableBanAllUpload(state);
    if (isBanAll) {
        conference?.disableVTFileUploadModeration();
    }
};
// File upload request and responses
export const requestSlot = (file) => (dispatch, getState) => {
    const state = getState();
    const { conference } = getConferenceState(state);
    dispatch(updateFileUpload({ ...file, status: 'pending' }));
    const { id, name, type, size } = file;
    conference?.vtFileUploadModerationRequestSlot({ id, name, type, size });
};
export const receiveSlot = (slotInfo) => (dispatch) => {
    dispatch(updateFileUpload({ ...slotInfo, status: 'ready' }));
};
export const receiveSlotError = (error) => (dispatch) => {
    dispatch(updateFileUpload({ ...error, status: 'error' }));
};
export const setPendingMessage = (message) => (dispatch) => {
    dispatch(_setPendingMessage(message));
    if (!message) {
        dispatch(emptyFileUpload());
    }
};
// Slice for file upload moderation
const vtFileUploadModeration = createSlice({
    name: 'features/vt-file-upload-moderation',
    initialState,
    reducers: {
        disableVTFileUploadModeration: (state, action) => {
            if (state.enabled) {
                state.enabled = false;
                state.actor = action.payload;
                state.canUpload = undefined;
                state.whitelist = {};
                state.blacklist = {};
            }
        },
        enableVTFileUploadModeration: (state, action) => {
            if (!state.enabled) {
                state.enabled = true;
                state.actor = action.payload;
                state.canUpload = undefined;
                state.whitelist = {};
                state.blacklist = {};
            }
        },
        localParticipantApproved: (state) => {
            state.canUpload = true;
        },
        localParticipantRejected: (state) => {
            state.canUpload = false;
        },
        participantApproved: (state, action) => {
            if (state.enabled) {
                state.whitelist[action.payload.jid] = true;
            }
            else {
                state.blacklist[action.payload.jid] = false;
            }
        },
        participantRejected: (state, action) => {
            if (state.enabled) {
                state.whitelist[action.payload.jid] = false;
            }
            else {
                state.blacklist[action.payload.jid] = true;
            }
        },
        addFileUpload: (state, action) => {
            state.files = [...state.files, action.payload];
        },
        updateFileUpload: (state, action) => {
            const { id } = action.payload;
            state.files = state.files.map(f => f.id === id ? { ...f, ...action.payload } : f);
        },
        removeFileUpload: (state, action) => {
            const removeFiles = state.files.filter(f => action.payload.includes(f.id));
            removeFiles.forEach(rmf => {
                if (rmf.status === "uploading" && rmf.controller) {
                    try {
                        rmf.controller.abort();
                    }
                    catch (e) {
                        console.error("Error when aborting upload file: ", e);
                    }
                }
            });
            state.files = state.files.filter(f => !action.payload.includes(f.id));
            if (state.pendingMessage)
                state.pendingMessage.files = state.pendingMessage?.files?.filter(f => !action.payload.includes(f.id));
        },
        emptyFileUpload: (state) => {
            state.files = [];
        },
        _setPendingMessage: (state, action) => {
            state.pendingMessage = action.payload;
        }
    }
});
ReducerRegistry.register('features/vt-file-upload-moderation', vtFileUploadModeration.reducer);
export const { disableVTFileUploadModeration, enableVTFileUploadModeration, addFileUpload, removeFileUpload, updateFileUpload, emptyFileUpload, _setPendingMessage, localParticipantApproved, localParticipantRejected, participantApproved, participantRejected } = vtFileUploadModeration.actions;
export default vtFileUploadModeration.reducer;
